import { Container, type Text } from "pixi.js";
import { Spine } from "@esotericsoftware/spine-pixi-v8";
import type { GameVisualAssets } from "../../assets/GameVisualAssets";
import { FEATURE_TRIGGER_TUNING } from "../../tuning/featureTriggerTuning";
import { SpinePanelMotion, hideSlotAttachments, socketContainerToBone } from "@slot/presentation";
import type { PresentationTaskRunner, SpineDriver, ViewportStore } from "@slot/presentation";
import { ModalChrome, bindTap, createLocalizedModalLabel, createModalLabel } from "@slot/game-shell";
import type { FeatureTriggerContent, FeatureTriggerPort, LocalizationService } from "@slot/game-shell";

const CONTAINER_SOCKET_BONE = "freespin win";
const GOLD = 0xffd600;
const NUMBER_GUIDE_SLOT = "number_placeholder";

/** `ModalLayer/GameModalContainer/FeatureTriggerPanel` geometry from the scene dump (authored y-up, stored y-down). */
const FEATURE_TRIGGER_GEOMETRY = {
  // The resting count at the grid's centre (see ModalChrome's `content`), measured 2026-10-04. Landscape: not yet.
  content: { width: 1534, height: 1133, y: { desktop: 99.7, mobile: -200, mobile_landscape: 99.7 }, shiftY: { desktop: -132.3, mobile: 53.0, mobile_landscape: 0 } },
  spine: { y: -66.5, scale: 1.105 },
  title: { y: -77.7, fontSize: 103.4 },
  amount: { y: 74.0, fontSize: 145.6 },
  subtitle: { y: 200.7, fontSize: 78.3 },
} as const;

/**
 * The feature-trigger view behind `FeatureTriggerPort`. The `freespins` Spine plays `in` → `idle` (loop)
 * on enter and `out` on exit, the dim + the "tap anywhere" hint fading on the chrome clock (the shell's
 * `ModalChrome` and presentation's `SpinePanelMotion`, decision 46). The
 * AnimatedContainer (count + title + subtitle) is socketed to the Spine's `freespin win` bone, so it
 * rides the in/out motion as one unit. The `number_placeholder` guide is hidden for good.
 */
export class FeatureTriggerView extends Container implements FeatureTriggerPort {
  private readonly skipButton: Container;
  private readonly chrome: ModalChrome;
  /** The freespins Spine's in / idle / out with the dim and the hint fading beside it. */
  private readonly motion: SpinePanelMotion;
  private readonly subtitleLabel: Text;

  public constructor(input: {
    assets: GameVisualAssets;
    viewport: ViewportStore;
    localization: LocalizationService;
    spineDriver: SpineDriver;
    runner: PresentationTaskRunner;
  }) {
    super({ label: "FeatureTriggerPanel" });
    const g = FEATURE_TRIGGER_GEOMETRY;
    this.chrome = new ModalChrome({
      label: "root",
      viewport: input.viewport,
      localization: input.localization,
      runner: input.runner,
      content: { ...g.content, limits: { desktop: { maxWidth: 1534 }, mobile: { maxWidth: 1534 }, mobile_landscape: {} } },
    });
    this.addChild(this.chrome);
    this.skipButton = this.chrome.skipButton;

    let skeleton: Spine | null = null;
    const data = input.assets.skeletons.get("animations/spine/freespins.skeleton");
    if (data) {
      skeleton = new Spine({ skeletonData: data, autoUpdate: false });
      skeleton.label = "spine";
      skeleton.position.set(0, g.spine.y);
      skeleton.scale.set(g.spine.scale);
      input.spineDriver.attach(skeleton);
      this.chrome.content.addChild(skeleton);
      hideSlotAttachments(skeleton, NUMBER_GUIDE_SLOT);
    }
    this.motion = new SpinePanelMotion({ surface: this.chrome, skeleton, runner: input.runner, tuning: FEATURE_TRIGGER_TUNING.anim });

    // AnimatedContainer (count + title + subtitle) — socketed to the Spine so it rides in/out as one unit.
    const animatedContainer = new Container({ label: "AnimatedContainer" });
    animatedContainer.position.set(0, g.spine.y);
    const { localization } = input;
    this.subtitleLabel = createModalLabel({ ...g.amount, color: GOLD, label: "AmountLabel" });
    animatedContainer.addChild(
      createLocalizedModalLabel({ localization, key: "featuretrigger.subtitle", fallback: "Free Spins", ...g.subtitle, color: GOLD, label: "SubTitle" }),
      createLocalizedModalLabel({ localization, key: "featuretrigger.title", fallback: "You Have Won", ...g.title, color: GOLD, label: "Title" }),
      this.subtitleLabel,
    );
    this.chrome.content.addChild(animatedContainer);
    if (skeleton) socketContainerToBone(skeleton, CONTAINER_SOCKET_BONE, animatedContainer, 1);
  }

  public show(): void {
    // Pre-seat the entry state BEFORE activating the root, so Pixi doesn't paint one frame at the
    // previous (loop-residue or exit-end) pose.
    this.motion.preSeat();
    this.chrome.visible = true;
  }

  public hide(): void {
    this.chrome.visible = false;
    this.motion.stop();
  }

  public setContent(content: FeatureTriggerContent): void {
    this.subtitleLabel.text = content.amount ?? "";
  }

  public setSkipEnabled(enabled: boolean): void {
    this.skipButton.visible = enabled;
  }

  public bindSkipTap(onPress: () => void): () => void {
    return bindTap(this.skipButton, onPress);
  }

  public async playEnterAnimation(): Promise<void> {
    await this.motion.enter(); // re-seats first: covers a playEnterAnimation with no prior show()
  }

  public async playExitAnimation(): Promise<void> {
    await this.motion.exit();
  }

  public async fadeInContinueText(): Promise<void> {
    await this.motion.fadeInContinueText();
  }

  public dispose(): void {
    this.chrome.dispose();
    this.destroy({ children: true });
  }

}
